import { FC, MouseEventHandler } from 'react';

import { useAtomValue } from 'jotai';

import { SearchDocument } from '@cfa/pathway-fe-shared/xp';

import { useBreakpointDown } from '@/hooks/useMediaQuery';

import { HeadingItem } from '@/utils/document-utils';
import { cn } from '@/utils/tailwind';

import { Breadcrumb } from '@/types/types';

import { CopyrightFooter } from '../common';

import TableOfContents from './TableOfContents';
import TridionTitle from './TridionTitle';

import { isTridionPreviewAtom } from '@/atoms/is-tridion-preview';
import DocumentCompleteButton from '@/routes/Layout/Header/RightHeaderContent/DocumentCompleteButton';

interface MobileViewProps {
  lastModified?: string;
  breadcrumbs: Breadcrumb[];
  /** Custom icon content to render */
  iconContent?: React.ReactNode;
  documentContent: React.ReactNode;
  tocItems: HeadingItem[];
  handleTocItemClick: MouseEventHandler<HTMLAnchorElement>;
  title: string;
  tags: string[];
  isPreview?: boolean;
  isTraining: boolean;
  language: string;
  type?: SearchDocument['type'];
}

/** Tridion document mobile view. */
const MobileView: FC<MobileViewProps> = ({
  lastModified,
  breadcrumbs,
  iconContent,
  documentContent,
  tocItems,
  handleTocItemClick,
  title,
  tags,
  isPreview = false,
  isTraining,
  language,
  type,
}) => {
  const isLgAndDown = useBreakpointDown('lg');
  const isTridionPreview = useAtomValue(isTridionPreviewAtom);

  const isPreviewNonResource = isPreview && !isTridionPreview;

  return (
    <div
      className={cn(
        'bg-base-2 shadow-elevation-4 flex max-w-[652px] flex-col',
        {
          'shadow-elevation-8': isPreviewNonResource,
          'border-secondary-base! border-t-4!': isTridionPreview,
        },
      )}
    >
      <TridionTitle iconContent={iconContent} tags={tags} title={title} />
      <TableOfContents
        className={cn('lg:static', {
          hidden: type === 'GAME',
        })}
        handleClick={handleTocItemClick}
        tocItems={tocItems}
        language={language}
      />
      <div
        className={cn('tridionDocument bg-base-0 pb-11!', {
          native: window.native.isNative,
          mobileView: !isTridionPreview || isLgAndDown,
          'w-full!': isTridionPreview,
        })}
      >
        <div>{documentContent}</div>
        {isTraining && (
          <div className="max-w-[652px] px-0 py-5 text-center">
            <DocumentCompleteButton />
          </div>
        )}
      </div>
      <CopyrightFooter
        breadcrumbs={isTraining ? [] : breadcrumbs}
        lastPublishedDate={lastModified ?? ''}
        isTraining={isTraining}
      />
    </div>
  );
};

export default MobileView;
